import { ClientLoaderFunctionArgs, useLoaderData } from "react-router";
import CustomError from "@components/Error/CustomError";
import { PulseContext } from "@features/pulse/utils/types";
import { MetaDescriptorProps } from "@features/seo/utils/types";
import { type Route } from "@route-types/Pulse/+types/PulsePage";
import { fetchPulseData } from "@routes/Pulse/server/Pulse.server";
import { fetchContext, ResponseType } from "@sphtech/web2-core/ssr";
import { createMetaTags } from "@sphtech/web2-seo-schema";
import { HttpStatusCode } from "axios";

import PulsePageContent from "./PulsePageContent";

const PulsePage = (): React.ReactElement => {
  const context = useLoaderData<PulseContext>();

  // StatusCode is not 200
  if (context && context.statusCode !== HttpStatusCode.Ok) {
    return <CustomError statusCode={context.statusCode} />;
  }

  // Check if payload is missing
  if (typeof context?.payload === "undefined") {
    return <CustomError statusCode={HttpStatusCode.NotFound} />;
  }

  const { data, gaProps, schema } = context.payload;

  return <PulsePageContent data={data} gaProps={gaProps} schema={schema} />;
};

export default PulsePage;

export const loader = async ({
  params,
}: Route.LoaderArgs): Promise<PulseContext> => {
  return await fetchPulseData(params.id);
};

export const meta = ({ loaderData }: Route.MetaArgs): MetaDescriptorProps[] => {
  const context = loaderData as PulseContext;

  if (context.type !== ResponseType.SUCCESS) return [];
  const data = context.payload;

  if (!data) return [];
  if (!data.metaTags) return [];

  return createMetaTags(data.metaTags);
};

export const clientLoader = async (args: ClientLoaderFunctionArgs) => {
  const result = (await fetchContext(args)) as {
    context?: PulseContext;
  };

  if (!result.context) return await args.serverLoader();

  return result.context;
};
